import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import app, { db } from "./firebase";
import { CuerpoProvider, useCuerpo } from "./context/CuerpoContext";

import LoginAdmin from "./admin/LoginAdmin";
import AdminPanel from "./admin/AdminPanel";
import MigracionHuasco from "./admin/MigracionHuasco";
import VistaGlobal from "./views/VistaGlobal";
import PartesLista from "./views/PartesLista";
import PartesEditar from "./views/PartesEditar";
import PartesEliminar from "./views/PartesEliminar";
import PartesRouter from "./partes/PartesRouter";
import PartesSeleccionCompania from "./views/PartesSeleccionCompania";
import ParteFormulario from "./views/ParteFormulario";
import LadingPrincipal from "./LadingPrincipal";
import CompaniaDesactivada from "./views/CompaniaDesactivada";
import CuerpoDesactivado from "./views/CuerpoDesactivado";
import CuerpoNoEncontrado from "./views/CuerpoNoEncontrado";
import CompaniaRoute from "./views/CompaniaRoute";
import InicioGeneral from "./views/InicioGeneral";
import OrganigramaPublico from "./views/OrganigramaPublico";

const DEFAULT_VIS = {
  landing_activa: true,
  global_activa: true,
  mensaje_desactivacion: "Esta página ha sido desactivada por la administración.",
  companias: {},
};

const VIS_CACHE_PREFIX = "bomberos_en_servicio_visibilidad_";

function leerVisibilidadCache(cuerpoId) {
  try {
    const raw = localStorage.getItem(`${VIS_CACHE_PREFIX}${String(cuerpoId || "").toLowerCase()}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_VIS, ...parsed, companias: parsed?.companias || {} };
  } catch (_) {
    return null;
  }
}

function guardarVisibilidadCache(cuerpoId, config) {
  try {
    if (!cuerpoId || !config) return;
    localStorage.setItem(
      `${VIS_CACHE_PREFIX}${String(cuerpoId).toLowerCase()}`,
      JSON.stringify(config)
    );
  } catch (_) {}
}


function AppRoutes() {
  const location = useLocation();
  const esRutaAdmin = location.pathname === "/admin-login" || location.pathname.startsWith("/admin");
  const esPortadaGeneral = location.pathname === "/";
  const { cuerpo, cuerpoId, cargandoCuerpo, cuerpoNoEncontrado } = useCuerpo();
  const [adminAutenticado, setAdminAutenticado] = useState(false);
  const [configVisibilidad, setConfigVisibilidad] = useState(null);

  useEffect(() => {
    const auth = getAuth(app);
    return onAuthStateChanged(auth, (user) => setAdminAutenticado(!!user));
  }, []);

  useEffect(() => {
    if (!cuerpoId) {
      setConfigVisibilidad(DEFAULT_VIS);
      return;
    }

    // Mostramos inmediatamente la última configuración conocida (o la predeterminada)
    // y refrescamos Firestore en segundo plano. Así la navegación no queda bloqueada.
    const cache = leerVisibilidadCache(cuerpoId);
    setConfigVisibilidad(cache || DEFAULT_VIS);

    const ref = doc(db, "configuracion_visibilidad", cuerpoId);
    return onSnapshot(
      ref,
      async (snap) => {
        if (snap.exists()) {
          const nueva = { ...DEFAULT_VIS, ...snap.data(), companias: snap.data()?.companias || {} };
          guardarVisibilidadCache(cuerpoId, nueva);
          setConfigVisibilidad(nueva);
          return;
        }
        // Compatibilidad: conserva la visibilidad antigua de Huasco hasta que se migre desde Administración.
        if (String(cuerpoId).toLowerCase() === "huasco") {
          try {
            const legacy = await getDoc(doc(db, "configuracion", "visibilidad"));
            if (legacy.exists()) {
              const ld = legacy.data() || {};
              const legacyConfig = {
                ...DEFAULT_VIS,
                landing_activa: ld.landing_activa !== false,
                global_activa: ld.global_activa !== false,
                mensaje_desactivacion: ld.mensaje_desactivacion || DEFAULT_VIS.mensaje_desactivacion,
                companias: {
                  kf83mx: ld.cia_kf83mx_activa !== false,
                  zl29qp: ld.cia_zl29qp_activa !== false,
                  rd77lt: ld.cia_rd77lt_activa !== false,
                  vb52cz: ld.cia_vb52cz_activa !== false,
                  wn64dy: ld.cia_wn64dy_activa !== false,
                },
              };
              guardarVisibilidadCache(cuerpoId, legacyConfig);
              setConfigVisibilidad(legacyConfig);
              return;
            }
          } catch (e) { console.warn("No se pudo leer visibilidad legacy:", e); }
        }
        guardarVisibilidadCache(cuerpoId, DEFAULT_VIS);
        setConfigVisibilidad(DEFAULT_VIS);
      },
      (error) => {
        console.error("Error leyendo visibilidad multi-Cuerpo:", error);
        // Si falla la red, mantenemos el cache/predeterminado ya mostrado.
        setConfigVisibilidad((actual) => actual || DEFAULT_VIS);
      }
    );
  }, [cuerpoId]);

  // Las rutas de administración NO dependen de la configuración pública del Cuerpo.
  // Esto evita que un admin recién creado quede atrapado en "Cargando configuración"
  // antes de que AdminPanel pueda leer su perfil y seleccionar su cuerpo_id.
  if (esRutaAdmin) {
    return (
      <Routes>
        <Route path="/admin-login" element={<LoginAdmin />} />
        <Route path="/admin" element={adminAutenticado ? <AdminPanel /> : <Navigate to="/admin-login" replace />} />
        <Route path="/admin/migracion-huasco" element={adminAutenticado ? <MigracionHuasco /> : <Navigate to="/admin-login" replace />} />
      </Routes>
    );
  }

  // La raíz del dominio es una portada general multi-Cuerpo.
  // No depende de un Cuerpo seleccionado ni de su configuración de visibilidad.
  if (esPortadaGeneral) {
    return <InicioGeneral />;
  }

  if (cargandoCuerpo) return <div style={{ padding: 20 }}>Cargando configuración...</div>;

  // Si el slug solicitado no corresponde a ningún Cuerpo registrado, no mostramos
  // una Landing vacía ni fabricamos un Cuerpo ficticio.
  if (cuerpoNoEncontrado) return <CuerpoNoEncontrado />;

  if (!configVisibilidad) return <div style={{ padding: 20 }}>Cargando configuración...</div>;

  const cuerpoInactivo = cuerpo?.activo === false;
  const landing = cuerpoInactivo ? <CuerpoDesactivado /> : (configVisibilidad.landing_activa !== false ? <LadingPrincipal /> : <CompaniaDesactivada mensaje={configVisibilidad.mensaje_desactivacion} />);
  const global = cuerpoInactivo ? <CuerpoDesactivado /> : (configVisibilidad.global_activa !== false ? <VistaGlobal /> : <CompaniaDesactivada mensaje={configVisibilidad.mensaje_desactivacion} />);

  return (
    <Routes>
      {/* Rutas públicas definitivas por Cuerpo. La raíz / se resuelve arriba como portada general. */}
      <Route path="/:cuerpoSlug" element={landing} />

      <Route
        path="/:cuerpoSlug/cia/:slug"
        element={cuerpoInactivo ? <CuerpoDesactivado /> : <CompaniaRoute configVisibilidad={configVisibilidad} />}
      />
      <Route path="/:cuerpoSlug/global" element={global} />
      <Route path="/:cuerpoSlug/organigrama" element={cuerpoInactivo ? <CuerpoDesactivado /> : <OrganigramaPublico />} />

      <Route path="/:cuerpoSlug/partes" element={<PartesSeleccionCompania />} />
      <Route path="/:cuerpoSlug/partes/nuevo" element={<ParteFormulario />} />
      <Route path="/:cuerpoSlug/partes/lista" element={<PartesLista />} />
      <Route path="/:cuerpoSlug/partes/editar" element={<PartesEditar />} />
      <Route path="/:cuerpoSlug/partes/eliminar" element={<PartesEliminar />} />
      <Route path="/:cuerpoSlug/partes/*" element={<PartesRouter />} />

      <Route path="/admin-login" element={<LoginAdmin />} />
      <Route path="/admin" element={adminAutenticado ? <AdminPanel /> : <Navigate to="/admin-login" replace />} />
      <Route path="/admin/migracion-huasco" element={adminAutenticado ? <MigracionHuasco /> : <Navigate to="/admin-login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <CuerpoProvider>
        <AppRoutes />
      </CuerpoProvider>
    </Router>
  );
}
